import { Link } from 'react-router-dom';
import Navigation from '../../components/Navigation';
import Footer from '../../components/Footer';
import SEOHead from '../../components/SEOHead';
import { getEssay } from '../../data/essays';
import { ArrowLeft } from 'lucide-react';

const essay = getEssay('tech-led-human-powered')!;

const TechLedHumanPowered = () => {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": essay.title,
    "description": essay.subtitle,
    "datePublished": essay.date,
    "dateModified": essay.dateModified ?? essay.date,
    "author": {
      "@type": "Person",
      "name": essay.author,
      "url": "https://www.linkedin.com/in/kent-frazier/",
    },
    "publisher": {
      "@type": "Organization",
      "name": "ParadoxEdge",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.paradoxedge.com/lovable-uploads/180210a9-9a73-4038-a36b-f373598ef48a.png",
      },
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.paradoxedge.com/essays/${essay.slug}`,
    },
  };

  return (
    <>
      <SEOHead
        title={`${essay.title} | Essays`}
        description={essay.subtitle}
        canonicalUrl={`/essays/${essay.slug}`}
        ogType="article"
        ogImage={essay.ogImage}
        ogImageAlt="A tech-led, human-powered world? An essay by Kent H. Frazier"
        publishedTime={essay.date}
        modifiedTime={essay.dateModified ?? essay.date}
        jsonLd={jsonLd}
      />
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1">
          <article className="py-20 sm:py-28">
            <div className="container-mobile max-w-[42rem]">
              {/* Back link */}
              <Link
                to="/essays"
                onClick={() => window.scrollTo(0, 0)}
                className="inline-flex items-center gap-2 text-sm text-foreground/90 hover:text-primary transition-colors mb-10"
              >
                <ArrowLeft className="w-4 h-4" />
                All essays
              </Link>

              {/* Header */}
              <header className="mb-12 sm:mb-16">
                <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-primary font-semibold mb-6">
                  Essay
                </p>
                <h1 className="mb-6">{essay.title}</h1>
                <p className="font-display text-xl sm:text-2xl text-foreground/95 leading-snug mb-8 italic">
                  {essay.subtitle}
                </p>
                <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground border-t border-border pt-6">
                  <span className="font-semibold text-foreground">By {essay.author}</span>
                  <span aria-hidden="true">·</span>
                  <time dateTime={essay.date}>{essay.dateDisplay}</time>
                  <span aria-hidden="true">·</span>
                  <span>{essay.readingMinutes} min read</span>
                </div>
              </header>

              {/* Body */}
              <div className="essay-prose">
                <p>WTF?</p>

                <p>Did I read that EY press release right?</p>

                <p>That must be a typo or misprint.</p>

                <p>Nope. That phrase is in the top key messages, above the CEO’s quote, in an approved corporate announcement.</p>

                <p>Earlier EY announcements put humans at the center of technology.</p>

                <p>What changed?</p>

                <p>How does technology lead? Technology can’t lead.</p>

                <p>Technology doesn’t possess judgment, discernment, wisdom, or accountability.</p>

                <p>It’s not accountable for the consequences of its output.</p>

                <p>To lead is to be answerable for what happens. Judgment, discernment, wisdom, and accountability are all inherent in what it means to lead.</p>

                <p>That is the work of a human being, not a machine.</p>

                <p>What has to be true for a “tech-led, human-powered world” to sound normal, let alone attract talented people?</p>

                <p>Is this an attempt to resolve an edgy paradox?</p>

                <p>EY tells the market they are an AI-forward company while telling their people that they still matter.</p>

                <p>How do people matter?</p>

                <p>Powered is fuel language. Batteries. Turbines. Food.</p>

                <p>Whatever was intended, that language positions humans as the fuel and the machine as the thing leading.</p>

                <p>Who wants to work for and report to an algorithm?</p>

                {/* Section break — the pivot of the piece */}
                <div className="not-prose my-16 sm:my-20 flex items-center justify-center gap-6">
                  <span className="h-px w-20 bg-primary/30" />
                  <span className="inline-block w-2 h-2 bg-primary/50 rotate-45" />
                  <span className="h-px w-20 bg-primary/30" />
                </div>

                <p>What if we restore agency and leadership to its rightful place, as a “human-led, tech-powered world”?</p>

                <p>What would have to be true for us to adopt that story as the future worth creating?</p>

                <p>Invest as rigorously to develop wisdom, discernment, judgment, and accountability as the way forward.</p>

                <p>Human capacities compound over time, and enhanced machine capability does not diminish them, it makes them exponentially more valuable.</p>

                <p>The human being is the precious resource worthy of investment. We are the source of the intention, the voice of wisdom, judgment, discernment, and the ones who stand accountable for the outcomes, with technology in service of humanity.</p>

                <p>Whether you subscribe to the language and lens of a tech-led, human-powered world or a human-led, tech-powered world, recognize that the language you use for your work is the world you end up living inside.</p>

                <p>Choose wisely.</p>
              </div>

              {/* Author block */}
              <aside className="mt-20 pt-10 border-t border-border">
                <p className="text-xs tracking-[0.25em] uppercase text-primary font-semibold mb-4">
                  About the author
                </p>
                <p className="text-foreground leading-relaxed">
                  Kent Frazier is the founder of ParadoxEdge. A former HR executive and Certified Integral Master Coach, he has spent three decades inside the work of leadership, partnering with senior leaders to develop the inner capacities and outer capabilities that meet the moment.
                </p>
                <div className="mt-8">
                  <a
                    href="https://calendly.com/kentfrazier/30-minutes"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground px-6 py-3 text-sm font-semibold rounded-full transition-all duration-300"
                  >
                    Begin a Conversation
                  </a>
                </div>
              </aside>
            </div>
          </article>
        </main>
        <Footer />
      </div>
    </>
  );
};

export default TechLedHumanPowered;
